import { get } from 'lodash';
import { DATA_TYPE_GMV, DATA_TYPE_UNITS } from '../../constants/analytics';

const isOverMillion = (value) => value >= 1_000_000;
const isOverThousand = (value) => value >= 1_000;

export const INTEGERS_ONLY = true;

function getCountAbbreviation(value) {
  if (isOverMillion(value)) return 'M';
  if (isOverThousand(value)) return 'K';
  return '';
}

export function graphNumberFormatter(value) {
  const suffix = getCountAbbreviation(value);
  const overMillion = isOverMillion(value);
  const overThousand = isOverThousand(value);

  if (overMillion) {
    value = value / 1_000_000;
  } else if (overThousand) {
    value = value / 1_000;
  }

  const number = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: overThousand && !overMillion ? 1 : 2,
  }).format(value);

  return `${number}${suffix}`;
}

export function graphCurrencyFormatter(value, currency = 'USD', integersOnly = false) {
  if (integersOnly && !Number.isInteger(value)) {
    return '';
  }

  const suffix = getCountAbbreviation(value);
  const overMillion = isOverMillion(value);
  const overThousand = isOverThousand(value);

  if (overMillion) {
    value = value / 1_000_000;
  } else if (overThousand) {
    value = value / 1_000;
  }

  const number = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: overThousand && !overMillion ? 1 : 2,
  }).format(value);

  return `${number}${suffix}`;
}

export function sizeFormatter(value) {
  const suffix = getCountAbbreviation(value) + 'B';
  const overMillion = isOverMillion(value);
  const overThousand = isOverThousand(value);
  if (overMillion) {
    value = value / 1_048_576;
  } else if (overThousand) {
    value = value / 1_024;
  }
  const number = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: overThousand && !overMillion ? 1 : 2,
  }).format(value);
  return `${number}${suffix}`;
}

export function graphUnitsFormatter(value, integersOnly = false) {
  if (integersOnly && !Number.isInteger(value)) {
    return '';
  }
  return value;
}

export function pieChartGraphData(data, retailerNamesByIds, currency) {
  const byRetailerId = {};
  const totalSums = {
    gmv: 0,
    units: 0,
    orders: 0,
  };

  const extractValueByMetricFromRawDayRecord = (dayRecord) => ({
    gmv: get(dayRecord, [DATA_TYPE_GMV.value, currency, 'all_orders'], 0),
    units: get(dayRecord, [DATA_TYPE_UNITS.value, 'all_orders'], 0),
    orders: get(dayRecord, 'total_orders_count', 0),
  });

  data?.forEach((datum) => {
    const values = extractValueByMetricFromRawDayRecord(datum);
    totalSums.gmv += values.gmv;
    totalSums.units += values.units;
    totalSums.orders += values.orders;

    const orderRecord = {
      gmv: datum.gmv,
      unit_count: datum.unit_count,
      total_orders_count: datum.total_orders_count,
    };

    if (!byRetailerId[datum.retailer_id]) {
      byRetailerId[datum.retailer_id] = {
        id: datum.retailer_id,
        name: retailerNamesByIds[datum.retailer_id],
        orders: [],
      };
    }
    byRetailerId[datum.retailer_id].orders.push(orderRecord);
  });

  return Object.values(byRetailerId).map((retailer) => {
    const totals = {
      gmv: {
        id: retailer.name,
        value: 0,
        total: 0,
      },
      units: {
        id: retailer.name,
        value: 0,
        total: 0,
      },
      orders: {
        id: retailer.name,
        value: 0,
        total: 0,
      },
    };

    if (totalSums.gmv) {
      const totalGmv = retailer.orders.reduce((sum, order) => {
        return sum + get(order, [DATA_TYPE_GMV.value, currency, 'all_orders'], 0);
      }, 0);

      totals.gmv.value = Number(((totalGmv / totalSums.gmv) * 100).toFixed(2));
      totals.gmv.total = Number((totalGmv / 100).toFixed(2));
    }

    if (totalSums.units) {
      const totalUnitsValue = retailer.orders.reduce((sum, order) => {
        return sum + get(order, [DATA_TYPE_UNITS.value, 'all_orders'], 0);
      }, 0);
      totals.units.value = totalSums.units ? Number(((totalUnitsValue / totalSums.units) * 100).toFixed(2)) : 0;
      totals.units.total = Number(totalUnitsValue);
    }

    if (totalSums.orders) {
      const totalOrdersValue = retailer.orders.reduce((sum, order) => {
        return sum + get(order, 'total_orders_count', 0);
      }, 0);
      totals.orders.value = Number(((totalOrdersValue / totalSums.orders) * 100).toFixed(2));
      totals.orders.total = Number(totalOrdersValue);
    }

    return totals;
  }, []);
}
